import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Truck, Plus, Trash2, CheckCircle2, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import Seo from "@/components/Seo";
import { DangerousGoods } from "@/components/delivery/DangerousGoods";
import { Bookings } from "@/components/delivery/Bookings";

const money = (n) => (n === null || n === undefined ? "—" : "$" + Number(n).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const blankPackage = { description: "", item_type: "Carton", quantity: 1, weight_kg: "", length_cm: "", width_cm: "", height_cm: "", dangerous_goods: false, un_number: "", packing_group: "" };
const ITEM_TYPES = ["Carton", "Pallet", "Skid", "Crate", "Satchel", "Panel", "Bundle", "Case"];

export default function Delivery() {
  const [status, setStatus] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState({ to_suburb: "", to_postcode: "", to_address_line1: "" });
  const [packages, setPackages] = useState([{ ...blankPackage }]);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("rates");
  const [dgProducts, setDgProducts] = useState([]);

  useEffect(() => {
    api.get("/machship/status").then(({ data }) => setStatus(data)).catch((e) => setStatus({ configured: false, authenticated: false, message: formatApiError(e, "Could not reach MachShip") }));
    api.get("/freight/config").then(({ data }) => {
      setWarehouses(data.warehouses || []);
      if (data.warehouses?.length) setOrigin(data.warehouses[0].id);
      const specs = data.carton_specs || {};
      setDgProducts(Object.entries(specs)
        .filter(([, item]) => (item.cartons || []).some((carton) => carton.dangerous_goods))
        .map(([id, item]) => ({ id, name: item.name })));
    }).catch(() => {});
  }, []);

  const setPack = (index, field, value) => setPackages((list) => list.map((p, i) => (i === index ? { ...p, [field]: value } : p)));

  const submit = async (e) => {
    e.preventDefault();
    const warehouse = warehouses.find((w) => w.id === origin);
    if (!warehouse) return toast.error("Choose a dispatch warehouse");
    setLoading(true);
    setQuote(null);
    try {
      const body = {
        from_suburb: warehouse.suburb,
        from_postcode: warehouse.postcode,
        to_suburb: destination.to_suburb.trim().toUpperCase(),
        to_postcode: destination.to_postcode.trim(),
        to_address_line1: destination.to_address_line1.trim() || null,
        packages: packages.map((p) => ({
          description: p.description.trim(),
          item_type: p.item_type,
          quantity: Number(p.quantity),
          weight_kg: Number(p.weight_kg),
          length_cm: Number(p.length_cm),
          width_cm: Number(p.width_cm),
          height_cm: Number(p.height_cm),
          dangerous_goods: p.dangerous_goods,
          un_number: p.dangerous_goods && p.un_number ? Number(p.un_number) : null,
          packing_group: p.dangerous_goods && p.packing_group ? Number(p.packing_group) : null,
        })),
      };
      const { data } = await api.post("/machship/quote", body);
      setQuote(data);
      if (!data.routes.length) toast.error("No route returned — freight quote required");
    } catch (err) {
      toast.error(formatApiError(err, "Could not price this consignment"));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "rounded-sm border border-ei-mist px-3 py-2 text-sm";

  return (
    <div>
      <Seo title="Delivery — EI Battery Workspace" />
      <span className="ei-eyebrow">Delivery</span>
      <h1 className="mt-1 text-2xl sm:text-3xl font-bold text-ei-navy">Gem Carriers freight</h1>
      <p className="mt-2 text-sm text-ei-graphite/70 max-w-2xl">Live rates straight from our MachShip account. Every figure below is a real carrier sell price including the fuel levy and GST — nothing here is estimated.</p>

      <div className="mt-6 bg-white border border-ei-mist rounded-md p-5" data-testid="machship-status">
        {status === null ? (
          <div className="flex items-center gap-2 text-sm text-ei-graphite/70"><Loader2 className="w-4 h-4 animate-spin" /> Checking the MachShip connection…</div>
        ) : (
          <div className="flex items-start gap-3">
            {status.authenticated ? <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0" /> : <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />}
            <div className="text-sm">
              <p className="font-semibold text-ei-navy" data-testid="machship-status-label">
                {status.authenticated ? "MachShip connected" : status.configured ? "API key saved but not accepted" : "No API key configured"}
              </p>
              <p className="mt-1 text-ei-graphite/70">{status.message}</p>
              {status.companies?.length > 0 && (
                <p className="mt-1 text-xs text-ei-graphite/60" data-testid="machship-company">Account: {status.companies.map((c) => `${c.name} (ID ${c.id})`).join(", ")}</p>
              )}
              <p className="mt-1 text-xs text-ei-graphite/50">The API key is held as a backend secret and is never shown here.</p>
            </div>
          </div>
        )}
      </div>

      <div className="mt-5 flex flex-wrap gap-2" data-testid="delivery-tabs">
        {[["rates", "Live rates"], ["dg", "Dangerous goods"], ["bookings", "Book with Gem"]].map(([key, label]) => (
          <button key={key} type="button" onClick={() => setTab(key)} data-testid={`delivery-tab-${key}`}
            className={`font-display text-sm font-bold px-4 py-2.5 rounded-sm min-h-[44px] border ${tab === key ? "bg-ei-navy text-ei-white border-ei-navy" : "text-ei-navy border-ei-navy/25 hover:border-ei-navy"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "dg" && <DangerousGoods products={dgProducts} />}
      {tab === "bookings" && <Bookings />}

      {tab === "rates" && <>
      <form onSubmit={submit} className="mt-6 bg-white border border-ei-mist rounded-md p-5 grid gap-4" data-testid="machship-quote-form">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="grid gap-1 text-xs font-semibold text-ei-graphite/70">Dispatch warehouse
            <select value={origin} onChange={(e) => setOrigin(e.target.value)} className={inputCls} data-testid="machship-warehouse">
              {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name} — {w.suburb} {w.postcode}</option>)}
            </select>
          </label>
          <label className="grid gap-1 text-xs font-semibold text-ei-graphite/70">Delivery address line (optional)
            <input value={destination.to_address_line1} onChange={(e) => setDestination((d) => ({ ...d, to_address_line1: e.target.value }))} className={inputCls} placeholder="12 Smith St" data-testid="machship-address" />
          </label>
          <label className="grid gap-1 text-xs font-semibold text-ei-graphite/70">Delivery suburb
            <input required value={destination.to_suburb} onChange={(e) => setDestination((d) => ({ ...d, to_suburb: e.target.value }))} className={inputCls} placeholder="SYDNEY" data-testid="machship-suburb" />
          </label>
          <label className="grid gap-1 text-xs font-semibold text-ei-graphite/70">Delivery postcode
            <input required pattern="\d{4}" value={destination.to_postcode} onChange={(e) => setDestination((d) => ({ ...d, to_postcode: e.target.value }))} className={`${inputCls} tnum`} placeholder="2000" data-testid="machship-postcode" />
          </label>
        </div>

        <div className="grid gap-3">
          {packages.map((p, index) => (
            <div key={index} className="border border-ei-mist/70 rounded-sm p-3 grid gap-2" data-testid={`machship-package-${index}`}>
              <div className="grid gap-2 sm:grid-cols-4">
                <input required value={p.description} onChange={(e) => setPack(index, "description", e.target.value)} placeholder="Package description" className={`${inputCls} sm:col-span-2`} data-testid={`machship-description-${index}`} />
                <select value={p.item_type} onChange={(e) => setPack(index, "item_type", e.target.value)} className={inputCls} data-testid={`machship-type-${index}`}>
                  {ITEM_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <input required type="number" min="1" value={p.quantity} onChange={(e) => setPack(index, "quantity", e.target.value)} placeholder="Qty" className={`${inputCls} tnum`} data-testid={`machship-quantity-${index}`} />
              </div>
              <div className="grid gap-2 sm:grid-cols-4">
                <input required type="number" step="0.1" min="0" value={p.length_cm} onChange={(e) => setPack(index, "length_cm", e.target.value)} placeholder="Length cm" className={`${inputCls} tnum`} data-testid={`machship-length-${index}`} />
                <input required type="number" step="0.1" min="0" value={p.width_cm} onChange={(e) => setPack(index, "width_cm", e.target.value)} placeholder="Width cm" className={`${inputCls} tnum`} data-testid={`machship-width-${index}`} />
                <input required type="number" step="0.1" min="0" value={p.height_cm} onChange={(e) => setPack(index, "height_cm", e.target.value)} placeholder="Height cm" className={`${inputCls} tnum`} data-testid={`machship-height-${index}`} />
                <input required type="number" step="0.1" min="0" value={p.weight_kg} onChange={(e) => setPack(index, "weight_kg", e.target.value)} placeholder="Gross kg" className={`${inputCls} tnum`} data-testid={`machship-weight-${index}`} />
              </div>
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <label className="flex items-center gap-2 text-ei-graphite/80">
                  <input type="checkbox" checked={p.dangerous_goods} onChange={(e) => setPack(index, "dangerous_goods", e.target.checked)} data-testid={`machship-dg-${index}`} /> Dangerous goods
                </label>
                {p.dangerous_goods && (
                  <>
                    <input type="number" value={p.un_number} onChange={(e) => setPack(index, "un_number", e.target.value)} placeholder="UN number" className={`${inputCls} tnum w-36`} data-testid={`machship-un-${index}`} />
                    <input type="number" min="1" max="4" value={p.packing_group} onChange={(e) => setPack(index, "packing_group", e.target.value)} placeholder="Packing group" className={`${inputCls} tnum w-40`} data-testid={`machship-pg-${index}`} />
                  </>
                )}
                {packages.length > 1 && (
                  <button type="button" onClick={() => setPackages((list) => list.filter((_, i) => i !== index))} className="ml-auto text-red-700 hover:bg-red-50 p-2 rounded-sm" aria-label="Remove package" data-testid={`machship-remove-${index}`}><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => setPackages((list) => [...list, { ...blankPackage }])} className="inline-flex items-center gap-2 font-display text-sm font-bold text-ei-navy border border-ei-navy/25 px-4 py-2.5 rounded-sm hover:border-ei-navy min-h-[44px]" data-testid="machship-add-package"><Plus className="w-4 h-4" /> Add package</button>
          <button type="submit" disabled={loading || !status?.authenticated} className="inline-flex items-center gap-2 font-display text-sm font-bold bg-ei-navy text-ei-white px-4 py-2.5 rounded-sm hover:bg-ei-ink min-h-[44px] disabled:opacity-60" data-testid="machship-get-rates">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />} Get live Gem rates
          </button>
        </div>
      </form>

      {quote && (
        <div className="mt-6" data-testid="machship-results">
          {quote.routes.length === 0 ? (
            <div className="bg-white border border-ei-mist rounded-md p-10 text-center" data-testid="machship-no-routes">
              <AlertTriangle className="w-10 h-10 mx-auto text-amber-500" />
              <p className="mt-3 font-semibold text-ei-navy">Freight quote required</p>
              <p className="mt-1 text-sm text-ei-graphite/70 max-w-xl mx-auto">{quote.reason}</p>
            </div>
          ) : (
            <div className="bg-white border border-ei-mist rounded-md overflow-x-auto">
              <table className="w-full text-sm min-w-[760px]">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-ei-graphite/60 border-b border-ei-mist">
                    <th className="px-4 py-3">Carrier / service</th>
                    <th className="px-4 py-3 text-right">Ex GST</th>
                    <th className="px-4 py-3 text-right">Fuel levy</th>
                    <th className="px-4 py-3 text-right">Inc GST</th>
                    <th className="px-4 py-3">ETA</th>
                    <th className="px-4 py-3 text-right">Billed kg</th>
                  </tr>
                </thead>
                <tbody>
                  {quote.routes.map((route, index) => (
                    <tr key={`${route.carrier}-${route.service}-${index}`} className="border-b border-ei-mist/60 last:border-0" data-testid={`machship-route-${index}`}>
                      <td className="px-4 py-3">
                        <span className="font-semibold text-ei-navy">{route.carrier}</span>
                        <span className="block text-xs text-ei-graphite/60">{route.service}{index === 0 ? " — cheapest" : ""}</span>
                      </td>
                      <td className="px-4 py-3 text-right tnum">{money(route.total_ex_tax)}</td>
                      <td className="px-4 py-3 text-right tnum text-ei-graphite/70">{money(route.fuel_levy)}</td>
                      <td className="px-4 py-3 text-right tnum font-semibold text-ei-navy" data-testid={`machship-total-${index}`}>{money(route.total_inc_tax)}</td>
                      <td className="px-4 py-3 text-xs text-ei-graphite/70">{route.eta_local ? new Date(route.eta_local).toLocaleDateString("en-AU") : "—"}{route.business_days != null ? ` (${route.business_days} business days)` : ""}</td>
                      <td className="px-4 py-3 text-right tnum text-ei-graphite/70">{route.total_weight_kg ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      </>}
    </div>
  );
}
